/**
 * Context Health Indicator — shows context utilization as a thin progress bar.
 * Updates in real-time via SSE events during streaming.
 */

interface ContextHealthIndicatorProps {
  utilization: number | null;  // percentage (0-100)
  totalTokens: number | null;
  tiers?: string | null;       // e.g. "E:L2 D:L1 M:L0 F:L2 K:L2 B:L0"
}

export function ContextHealthIndicator({ utilization, totalTokens, tiers }: ContextHealthIndicatorProps) {
  if (utilization === null || utilization === 0) return null;

  const percent = Math.round(utilization);
  const color = percent < 60
    ? 'bg-lime-500'
    : percent < 75
      ? 'bg-yellow-500'
      : 'bg-red-500';

  const textColor = percent < 60
    ? 'text-lime-600 dark:text-lime-400'
    : percent < 75
      ? 'text-yellow-600 dark:text-yellow-400'
      : 'text-red-600 dark:text-red-400';

  return (
    <div className="px-3 py-1.5 border-b border-gray-200 dark:border-gray-700/50">
      <div className="flex items-center justify-between text-[10px] font-mono">
        <span className={textColor}>
          CTX {percent}%
          {tiers && (
            <span className="ml-1.5 text-gray-400 dark:text-gray-500">{tiers}</span>
          )}
        </span>
        <span className="text-gray-400 dark:text-gray-500">
          {totalTokens ? `${(totalTokens / 1000).toFixed(1)}k tokens` : ''}
        </span>
      </div>
      <div className="w-full h-0.5 bg-gray-200 dark:bg-gray-700 rounded-full mt-0.5">
        <div
          className={`h-full rounded-full transition-all duration-500 ${color}`}
          style={{ width: `${Math.min(percent, 100)}%` }}
        />
      </div>
    </div>
  );
}
